/* eslint-disable no-unused-vars */
import React, { useCallback } from "react";
import {
  UserCredential,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { AnyAction, Dispatch } from "redux";
import { NavigateFunction, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../Redux/store";
import {
  FirebaseAuthServiceErrorCode,
  auth,
  getJWTToken,
  registerWithEmailAndPassword,
  signInWithGoogle,
} from "../../Services/FirebaseService";
import Client from "../../Objects/models/Client";
import Teachr from "../../Objects/models/Teachr";
import { FirebaseAuthMethodEnum } from "../../Enum/FirebaseAuthMethodEnum";
import {
  fetchImageProfile,
  userClientSetAccountAction,
  userClientUnSetAccountAction,
  userFirebaseSetAccountAction,
  userFirebaseUnSetAccountAction,
  userTeachrSetAccountAction,
  userTeachrUnSetAccountAction,
  userUnSetCooperativeForm,
  userUnSetCurrentProfile,
  userUnSetLoad,
} from "../../Redux/Actions/UserActions";
import TeachrRepository from "../../Repositories/TeachrRepository";
import ApiService from "../../Services/ApiService";
import ClientRepository from "../../Repositories/ClientRepository";
import Functions from "../../Helpers/Functions";
import { FormState } from "../../Objects/interfaces/AppInterface";
import { SHOW_ERROR, SHOW_INFO, SHOW_WARNING } from "../../Redux/Reducers/MessageReducers";
import {
  resetActiveCourseAction,
  resetAmountDurationAction,
  resetInfoChartAction,
  resetNextCourseAction,
  resetPastCourseAction,
  resetPendingCourseAction,
  resetSubjectAction,
} from "../../Redux/Actions/PunctualCourseActions";
import { getActiveAppBrandConfig, getActiveBrandConfig, getActivePrestataireBrandConfig } from "../../config/environment.config";
import { FormInterface } from "../../Pages/Auth/Login";
import { getBrandConfig } from "../../config/brandConfig";
import { resetActiveClients, resetAllClients } from "../../Redux/Actions/StudentActions";
import { RESET_AE_INFO } from "../../Redux/Reducers/ProcurationAeReducers";
import { RESET_SAP_INFO } from "../../Redux/Reducers/ProcurationSapReducers";
// import { RESET_AE_INFO } from "../../Redux/Reducers/ProcurationAeReducers";
// import { RESET_SAP_INFO } from "../../Redux/Reducers/ProcurationSapReducers";

interface useAuthFnProps {
  setLoading: (value: boolean) => void,
  form?: FormState | unknown,
  validate?: () => boolean,
  course?: any,
}

export const persistanceConnexion = (remember: boolean, fn: () => void) => {
  auth.setPersistence(!remember ? browserSessionPersistence : indexedDBLocalPersistence).then(() => { fn() })
}

export function useAuthFn(initialState: useAuthFnProps) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { form, validate, setLoading } = initialState;
  const remember = (form as FormInterface)?.remember || false;
  const parseDisplayName = (displayName: string | null) => {
    if (!displayName) return { firstName: "", lastName: "" };
    const [firstName = "", lastName = ""] = displayName.split(" ");
    return { firstName, lastName };
  };
  //Gérer les erreur lors de l'inscription
  const handleError = (code: string, dispatch: Dispatch<AnyAction>) => {
    let message = "Quelque chose s'est mal passé!";
    if (code === FirebaseAuthServiceErrorCode.EMAIL_ALREADY_UN_USE) {
      message = "Cette adresse email est déjà enregistrée. Veuillez en saisir une autre.";
    }
    dispatch({ type: SHOW_WARNING, payload: message });
  };
  //Finaliser l'inscription d'un user
  const finalizeSignUp = async (
    userObject: Teachr | Client,
    userCredential: UserCredential,
    isTeachr: boolean,
    FirebaseAuthMethodEnum: any,
    dispatch?: Dispatch<AnyAction>,
    remember?: boolean,
    type?: string,
    userAuth?: string
  ) => {
    if (dispatch) {
      const jwtToken = await getJWTToken(userCredential.user);
      const serializedUserObject = JSON.parse(JSON.stringify(userObject));
      dispatch(
        isTeachr
          ? userTeachrSetAccountAction(serializedUserObject)
          : userClientSetAccountAction(serializedUserObject)
      );
      if (remember) {
        await auth.setPersistence(browserSessionPersistence);
      }
      if (isTeachr) {
        dispatch(
          userFirebaseSetAccountAction(
            FirebaseAuthMethodEnum,
            JSON.parse(JSON.stringify(userCredential))
          )
        );
        dispatch({
          type: type,
          payload: {
            teachr: serializedUserObject,
            authorization: `${userAuth} ${jwtToken}`,
          },
        });
      } else {
        // Espace client : mêmes dispatches. Attention, PAS de enableAuth ici :
        // le profil n'existe pas encore en base, et un header Authorization
        // avec un utilisateur inconnu fait répondre 403 au backend (POST
        // /clients est ouvert aux anonymes). L'auth est activée à l'étape 2,
        // après la création du profil.
        dispatch(
          userFirebaseSetAccountAction(
            FirebaseAuthMethodEnum,
            JSON.parse(JSON.stringify(userCredential))
          )
        );
      }
    }
  };
  //Changer les valeurs des Inputs à l'inscription
  const handleInputChange = (
    key: keyof FormState,
    _value: string | boolean,
    setForm: (value: React.SetStateAction<FormState>) => void
  ) => {
    setForm((prev) => ({ ...prev, [key]: _value }));
  };
  //Vérifier l'existence d'un user
  const checkExistingUser = async (isTeachr: boolean) => {
    try {
      if (isTeachr) {
        return await TeachrRepository.getCurrentTeachr();
      } else {
        return await ClientRepository.getCurrentClient();
      }
    } catch {
      return null;
    }
  };
  const performSignOut = useCallback(async (isTeachr: boolean = true, route?: string) => {
    try {
      await signOut(auth);
      ApiService.disableAuth();
      dispatch({ type: "DELETE_TEACHR_INFO", payload: null });
      dispatch({ type: "RESET_COURSE_DECLARATION_INFO", payload: null });
      dispatch({ type: "RESET_END_DECLARE_COURSE", payload: null });
      dispatch(
        isTeachr
          ? userTeachrUnSetAccountAction()
          : userClientUnSetAccountAction()
      );
      dispatch(userFirebaseUnSetAccountAction());
      dispatch(resetActiveCourseAction());
      dispatch(resetPastCourseAction());
      dispatch(resetNextCourseAction());
      dispatch(resetInfoChartAction());
      dispatch(userUnSetCurrentProfile());
      dispatch(resetAllClients());
      dispatch(resetActiveClients());
      dispatch(resetPendingCourseAction());
      dispatch(resetAmountDurationAction());
      dispatch(resetSubjectAction());
      dispatch(userUnSetCooperativeForm());
      dispatch({ type: RESET_AE_INFO });
      dispatch({ type: RESET_SAP_INFO });
      dispatch(userUnSetLoad());
      if (route) navigate(route);
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  }, [dispatch, navigate]);
  
  //Inscription via google
  const handleGoogleSignUp = useCallback(async (
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    isTeachr: boolean,
    dispatch: Dispatch<AnyAction>,
    navigate: NavigateFunction,
    teachrObject?: Teachr | any,
    remember?: boolean,
    type?: string,
    userAuth?: string,
    // route?: string
  ) => {
    setLoading(true);

    try {
      const request = await signInWithGoogle();
      if (!request) return;

      const user = request.user;

      try {
        const userAuths = await TeachrRepository.getCurrentUser(user.uid);
        if (userAuths) {
          return Swal.fire({
            icon: "warning",
            title: "Oops...",
            text: "Cette adresse email est déjà enregistrée. Veuillez en saisir une autre.",
            timer: 2000,
            willClose: () => performSignOut(),
          });
        }
      } catch (error: any) {
        if (JSON.parse(JSON.stringify(error)).data.error === "user not found") {
          // If new user, process sign-up
          if (request.providerId === "google.com") {
            const { firstName, lastName } = parseDisplayName(user?.displayName);
            teachrObject.firstName = firstName;
            teachrObject.lastName = lastName;
            teachrObject.mail = user.email;
            // finalizeSignUp gère lui-même la différence teachr / client
            await finalizeSignUp(
              teachrObject,
              request,
              isTeachr,
              FirebaseAuthMethodEnum.GOOGLE,
              dispatch,
              remember,
              type,
              userAuth
            );
            const result = {
              useCredential: request,
              userObject: teachrObject,
            };
            return result;
          }
        } else {
          setLoading(false);
          handleError(error?.code, dispatch);
        }
      }
    } catch (error: any) {
      setLoading(false);
      handleError(error?.code, dispatch);
    } finally {
      setLoading(false);
    }
  }, [])
  //Inscription via l'adresse e-mail et le mot de passe
  const handleMailSignUp = useCallback(async (
    e: any,
    validate: () => boolean,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    mail: string,
    password: string,
    isTeachr: boolean,
    dispatch: Dispatch<AnyAction>,
    phone?: string,
    userObject?: Teachr | Client | any,
    type?: string,
    userAuth?: string,
    navigate?: NavigateFunction,
    remember?: boolean
  ) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const request = await registerWithEmailAndPassword(mail, password);
      if (!request) return;

      // if (isTeachr) {
      userObject.mail = mail;
      userObject.phone = Functions.formatPhoneNumber(phone!);
      await finalizeSignUp(
        userObject,
        request,
        isTeachr,
        FirebaseAuthMethodEnum.MAIL,
        dispatch,
        remember,
        type,
        userAuth
      );
      // }
      return request;
    } catch (error: any) {
      setLoading(false);
      if (error?.code === FirebaseAuthServiceErrorCode.EMAIL_ALREADY_UN_USE) {
        // Compte Firebase déjà créé : soit un vrai doublon, soit une
        // inscription abandonnée avant l'étape 2. On tente de se connecter
        // avec le mot de passe saisi pour trancher.
        try {
          const credential = await signInWithEmailAndPassword(getAuth(), mail, password);
          try {
            await TeachrRepository.getCurrentUser(credential.user.uid);
            // profil déjà en base : vrai doublon, direction la connexion
            await signOut(auth);
            dispatch({ type: SHOW_WARNING, payload: "Cette adresse e-mail est déjà associée à un compte. Veuillez vous connecter." });
            if (navigate) navigate("/");
          } catch (lookupError: any) {
            if (JSON.parse(JSON.stringify(lookupError))?.data?.error === "user not found") {
              // compte orphelin : on reprend l'inscription à l'étape 2, sans
              // que l'utilisateur ait quoi que ce soit à refaire
              userObject.mail = mail;
              userObject.phone = Functions.formatPhoneNumber(phone!);
              await finalizeSignUp(
                userObject,
                credential,
                isTeachr,
                FirebaseAuthMethodEnum.MAIL,
                dispatch,
                remember,
                type,
                userAuth
              );
              return credential;
            }
            await signOut(auth);
            handleError(error?.code, dispatch);
            if (navigate) navigate("/");
          }
        } catch {
          // mot de passe différent : le compte appartient peut-être à quelqu'un d'autre
          dispatch({ type: SHOW_WARNING, payload: "Cette adresse e-mail est déjà utilisée. Si c'est la vôtre, connectez-vous ou utilisez « Mot de passe oublié ? »." });
          if (navigate) navigate("/");
        }
      } else {
        handleError(error?.code, dispatch);
      }
    } finally {
      setLoading(false);
    }
  }, [])

  const handleSetAndRedirectUserConnect = useCallback((userCredential: UserCredential, authMethod: any, userObject?: Teachr | Client, isTeachr?: boolean) => {
    getJWTToken(userCredential.user).then((jwtToken) => {
      TeachrRepository.getCurrentUser(userCredential.user.uid).then((response: any) => {
        if (response?.role === "teachr") {
          ApiService.enableAuth(undefined, undefined, `teachr ${jwtToken}`);
          TeachrRepository.getCurrentTeachr().then((teachr) => {
            if (teachr) {
              const serializedTeachr = JSON.parse(JSON.stringify(teachr));
              dispatch(userFirebaseSetAccountAction(authMethod, JSON.parse(JSON.stringify(userCredential))));
              dispatch(userTeachrSetAccountAction(serializedTeachr));
              dispatch({ type: 'SET_TEACHR_INFO', payload: { teachr: serializedTeachr, authorization: `teachr ${jwtToken}` } });
              if (serializedTeachr?.profilePicture && serializedTeachr?.profilePicture?.id) {
                const idPicture = teachr.profilePicture.id
                dispatch(fetchImageProfile(idPicture))
              }
              setLoading(false)
              return navigate(`/${getActiveBrandConfig()}/tableau-de-bord`);
            }
          }).catch(async () => {
            await signOut(auth);
            ApiService.disableAuth();
            setLoading(false)
            dispatch({ type: SHOW_ERROR, payload: "Ce compte n'existe pas." });
            dispatch({ type: 'HIDE_LOADER', payload: "" });
            return navigate(`/${getActiveBrandConfig()}/register`);
          });
        } else {
          ApiService.enableAuth(undefined, undefined, `client ${jwtToken}`);
          ClientRepository.getCurrentClient().then((client) => {
            if (client) {
              const serializedClient = JSON.parse(JSON.stringify(client));
              dispatch(userFirebaseSetAccountAction(authMethod, JSON.parse(JSON.stringify(userCredential))));
              dispatch(userClientSetAccountAction(serializedClient));
              dispatch({ type: 'HIDE_LOADER', payload: "" });
              setLoading(false);
              // Espace client : l'accueil du tableau de bord est la page d'atterrissage
              return navigate(`/${getActiveBrandConfig()}/tableau-de-bord`);
            }
          }).catch(async () => {
            await signOut(auth);
            ApiService.disableAuth();
            setLoading(false);
            dispatch({ type: SHOW_ERROR, payload: "Ce compte n'existe pas." });
            dispatch({ type: 'HIDE_LOADER', payload: "" });
            return navigate(`/${getActiveBrandConfig()}/register?=client`);
          });
        }
      }).catch(async (error: any) => {
        const code = JSON.parse(JSON.stringify(error))?.data?.error;
        const message = JSON.parse(JSON.stringify(error))?.data?.description;
        // console.warn("🚀 ~ file: useAuthFn.tsx:333 ~ handleSetAndRedirectUserConnect ~ error:", code) // TODO: remove
        // Phone user not in DB → redirect to registration without signing out
        if (code === "user not found" && authMethod === FirebaseAuthMethodEnum.PHONE) {
          setLoading(false);
          const teachrFlag = isTeachr ?? true;

          await finalizeSignUp(
            userObject ? userObject : ({} as Teachr | Client),
            userCredential,
            teachrFlag,
            FirebaseAuthMethodEnum.PHONE,
            dispatch,
            remember,
            getBrandConfig().name,
            teachrFlag ? 'SET_TEACHR_INFO' : undefined
          );
          navigate(teachrFlag
            ? `/${getActiveBrandConfig()}/register`
            : `/${getActiveBrandConfig()}/register?=client`
          );
          return;
        }

        
        setLoading(false);
        switch (code) {
          case "More than one result was found for query although one row or none was expected.":
            await signOut(auth);
            ApiService.disableAuth();
            dispatch({ type: SHOW_ERROR, payload: message });
            break;
          case "user not found":
            // Inscription abandonnée après l'étape 1 (mail ou Google) : on
            // garde la session Firebase et on reprend à l'étape 2, le choix
            // du profil (client ou prestataire) se fait sur /register.
            dispatch(userFirebaseSetAccountAction(authMethod, JSON.parse(JSON.stringify(userCredential))));
            dispatch({ type: SHOW_INFO, payload: "Votre inscription n'a pas été finalisée. Reprenons là où vous vous êtes arrêté." });
            navigate(`/${getActiveBrandConfig()}/register`);
            break;
          default:
            await signOut(auth);
            ApiService.disableAuth();
            dispatch({ type: SHOW_ERROR, payload: `Erreur inconnue, veuillez contacter le service client de ${getActiveBrandConfig()}` });
            break;
        }
      });
    });
  }, [dispatch, navigate, remember, setLoading]);

  const handleLoginWithGoogle = useCallback((rememberOverride?: boolean) => {
    persistanceConnexion(rememberOverride ?? remember, () => {
      signInWithGoogle().then((userCredential) => {
        setLoading(true);
        handleSetAndRedirectUserConnect(userCredential, FirebaseAuthMethodEnum.GOOGLE);
      }).catch((error: FirebaseAuthServiceErrorCode | any) => {
        setLoading(false);
        let errorMessage: string;
        switch (error?.code || error) {
          case FirebaseAuthServiceErrorCode.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL:
            errorMessage = "Veuillez utiliser l'authentification par email et spécifier le mot de passe associé.";
            break;
          case FirebaseAuthServiceErrorCode.INVALID_USER_TOKEN:
            errorMessage = "Erreur lors de l'authentification. Veuillez essayer à nouveau.";
            break;
          case FirebaseAuthServiceErrorCode.USER_DISABLED:
            errorMessage = "Le compte a été désactivé.";
            break;
          case FirebaseAuthServiceErrorCode.USER_NOT_FOUND:
            errorMessage = "Aucun utilisateur trouvé.";
            break;
          case FirebaseAuthServiceErrorCode.GOOGLE_PLAY_SERVICES_NOT_AVAILABLE:
            errorMessage = "Vous devez installer les Google Play Services sur votre téléphone.";
            break;
          case FirebaseAuthServiceErrorCode.GOOGLE_IN_PROGRESS:
            errorMessage = "La connexion à votre compte est en cours.";
            break;
          case FirebaseAuthServiceErrorCode.NETWORK_REQUEST_FAILED:
            errorMessage = "Erreur de réseau. Veuillez vous connecter à internet, ou réessayer plus tard.";
            break;
          case FirebaseAuthServiceErrorCode.GOOGLE_UNKNOWN:
            errorMessage = "Erreur inconnue. Si le problème persiste, contactez-nous.";
            break;
          case FirebaseAuthServiceErrorCode.GOOGLE_SIGN_IN_CANCELLED:
            errorMessage = "La connexion à votre compte est annulée.";
            break;
          default:
            errorMessage = error + " - Merci de vous reconnecter, ou contactez le service client.";
            break;
        }
        dispatch({ type: SHOW_ERROR, payload: errorMessage });
      })
    });
  }, [dispatch, handleSetAndRedirectUserConnect, remember, setLoading])

  const handleLoginWithMailAndPwd = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    if (validate && !validate()) {
      setLoading(false);
      return;
    }
    setLoading(true)
    persistanceConnexion(remember, () => {
      const authInstance = getAuth();
      const email = (form as any)?.email || '';
      const password = (form as any)?.password || '';
      signInWithEmailAndPassword(authInstance, email, password).then((userCredential) => {
        setLoading(true);
        handleSetAndRedirectUserConnect(userCredential, FirebaseAuthMethodEnum.MAIL);
      }).catch((error: FirebaseAuthServiceErrorCode | any) => {
        let errorMessage: string;
        setLoading(false);

        switch (error.code) {
          case FirebaseAuthServiceErrorCode.USER_NOT_FOUND:
            errorMessage = `Aucun utilisateur n'existe avec cette adresse e-mail. Veuillez vous inscrire en tant que ${getActivePrestataireBrandConfig()} chez ${getActiveAppBrandConfig()}, ou en tant que client.`;
            break;
          case FirebaseAuthServiceErrorCode.USER_LOGIN_INVALID:
            errorMessage = "Identifiants invalides. Veuillez vérifier votre adresse e-mail et votre mot de passe.";
            break;
          case FirebaseAuthServiceErrorCode.NETWORK_REQUEST_FAILED:
            errorMessage = "Erreur de réseau. Veuillez vous connecter à internet, ou réessayer plus tard.";
            break;
          case FirebaseAuthServiceErrorCode.USER_DISABLED:
            errorMessage = "L'adresse mail a été désactivée.";
            break;
          case FirebaseAuthServiceErrorCode.WRONG_PASSWORD:
            errorMessage = "Le mot de passe est invalide.";
            break;
          case FirebaseAuthServiceErrorCode.INVALID_EMAIL:
            errorMessage = "L'adresse mail est invalide.";
            break;
          default:
            errorMessage = `Une erreur inconnue est survenue. Merci de vous reconnecter, ou contactez le service client au ${process.env.REACT_APP_MAIL_SUPPORT}.`;
            break;
        }
        dispatch({ type: SHOW_ERROR, payload: errorMessage });
      })
      //   .finally(() => {
      //   setLoading(false);
      // });
    });
  }
    , [dispatch, form, handleSetAndRedirectUserConnect, remember, setLoading, validate])

  return {
    handleInputChange,
    checkExistingUser,
    performSignOut,
    handleGoogleSignUp,
    handleMailSignUp,
    handleSetAndRedirectUserConnect,
    // loginWithMailAndPwd,
    handleLoginWithGoogle,
    handleLoginWithMailAndPwd
  };
}
